import React, { useMemo } from 'react';
import { TextInput, Checkbox, Button, useToast } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { FormFields } from 'components/EmployeeOnboarding/ContractorSelfOnboarding/types';
import { getKycValidationSchema } from 'components/EmployeeOnboarding/ContractorSelfOnboarding/utils';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { routePaths } from 'components/Routes/data';
import useReduxSelector from 'utils/useReduxSelector';
import { isContractorsPanValidationEnabled } from 'utils/getContractorText';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSavedContractorData } from './hooks/useSavedContractorData';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { useMutation } from 'react-query';
import { VERIFY_PAN } from 'api/employeeOnboarding';
import { useUserId } from 'components/EmployeeDetails/BankAccountDetails/hooks/useUserId';
import api from 'api';
import { useHistory } from 'react-router-dom';

export const KycInfo: React.FC = () => {
  const {
    employeeDetails: { basicDetails },
  } = useSavedContractorData();
  const history = useHistory();

  const userId = useUserId();

  const toast = useToast();

  const featureFlags = useReduxSelector(loggedInUserSelectors.features);
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;

  const isContractorPanValidationEnabled = isContractorsPanValidationEnabled({
    isRxCurrentAccountMerchant,
    featureFlags,
  });

  const {
    formData,
    register,
    validate,
    setIsSubmitting,
    isSubmitting,
    setFieldError,
    setFieldValue,
    errors,
  } = useFormWithProvider<FormFields>();

  const isPanVerified = formData.panVerificationStatus;

  const mutatePanVerification = useMutation({
    mutationKey: [VERIFY_PAN, userId],
    mutationFn: api.employeeOnboarding.verifyPan,
    onSuccess: (response) => {
      if (response.data.validation_status === 'valid') {
        setFieldValue('panVerificationStatus', true);
      } else {
        setFieldError('pan', response.data.error);
        toast.show({
          content: 'Invalid PAN details',
          color: 'negative',
        });
      }
      setIsSubmitting(false);
    },
    onError: (error: typeof AppError) => {
      setFieldError('pan', parseGraphqlErrorFromExtensions(error));
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  const handlePanVerification = async () => {
    if (!validate(getKycValidationSchema({ isContractorPanValidationEnabled }))) {
      return;
    }
    setIsSubmitting(true);
    mutatePanVerification.mutate({
      pan: formData.pan!,
      name: basicDetails?.name!,
      employee_type: 'employee',
    });
  };

  const handleConfirm = () => {
    history.push(routePaths.employeeOnboarding.contractorSelfOnboarding.bankDetails);
  };

  const isFormValid = useMemo(
    () =>
      validate(getKycValidationSchema({ isContractorPanValidationEnabled }), {
        sideEffects: false,
      }),
    [validate, isContractorPanValidationEnabled],
  );

  const canSubmitForm = isFormValid && isPanVerified;

  return (
    <StyledForm>
      <FormFieldsWrapper>
        <FormHeading
          title="KYC Information"
          description="We need to verify your identity and ensure secure account management."
        />
        <BorderedBox>
          <TextInput
            {...register('pan')}
            label="PAN"
            placeholder="ABCPE1234F"
            size="medium"
            necessityIndicator="required"
            isRequired
            isDisabled={isPanVerified}
            helpText="We need your PAN number to calculate your income tax."
            validationState={errors.pan ? 'error' : isPanVerified ? 'success' : 'none'}
            successText={isPanVerified ? 'PAN Verified' : undefined}
            autoFocus
          />

          <TextInput
            {...register('aadharNumber')}
            label="Aadhar Number"
            placeholder="1234 5678 9012"
            size="medium"
            necessityIndicator={isContractorPanValidationEnabled ? 'required' : undefined}
            isRequired={isContractorPanValidationEnabled}
          />

          <Checkbox
            {...register('verificationConsent', 'checkbox')}
            size="small"
            isRequired
            isDisabled={isPanVerified}>
            I consent to the validation of my details
          </Checkbox>
        </BorderedBox>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button
          variant="tertiary"
          size="medium"
          onClick={() => (window.location.href = routePaths.phpRoutes.dashboard)}>
          Back
        </Button>
        {isPanVerified ? (
          <Button
            variant="primary"
            size="medium"
            onClick={handleConfirm}
            isDisabled={!canSubmitForm}>
            Confirm
          </Button>
        ) : (
          <Button
            variant="primary"
            size="medium"
            onClick={handlePanVerification}
            isDisabled={!formData.verificationConsent}
            isLoading={isSubmitting}>
            Continue
          </Button>
        )}
      </FormFooterWrapper>
    </StyledForm>
  );
};
